/**
 * MissingGuildPlaceholder.jsx — Sprint 16 issue #314.
 *
 * Thematic empty-state for guild rows where we have no sale data.
 * Lazy-loads "what we know" facts from /api/v2/guilds/:slug/known
 * on first hover and renders them in a CRT-styled tooltip.
 *
 * Usage:
 *   <MissingGuildPlaceholder guildSlug="Some Guild" />
 *   <MissingGuildPlaceholder guildSlug={g.name} label="No sales yet" />
 *
 * Privacy: this component never renders user-level data even if the
 * backend mistakenly leaked some — see the explicit fact-kind allowlist
 * in `renderFact`. New fact kinds must be added there before they show.
 */

import { createSignal, Show, For } from 'solid-js';
import './MissingGuildPlaceholder.css';

const cache = new Map(); // slug → { at, data }
const TTL_MS = 60_000;

async function loadKnown(slug) {
  const key = slug.toLowerCase();
  const hit = cache.get(key);
  if (hit && Date.now() - hit.at < TTL_MS) return hit.data;
  let data = null;
  try {
    const r = await fetch(`/api/v2/guilds/${encodeURIComponent(slug)}/known`, {
      credentials: 'include',
    });
    if (r.ok || r.status === 404) {
      data = await r.json().catch(() => null);
    }
  } catch {
    /* tolerate */
  }
  cache.set(key, { at: Date.now(), data });
  return data;
}

function fmtFactValue(fact) {
  if (fact.kind === 'sales-count' || fact.kind === 'seller-count') {
    return new Intl.NumberFormat().format(fact.value);
  }
  return String(fact.value);
}

export default function MissingGuildPlaceholder(props) {
  const slug = () => String(props.guildSlug || '').trim();
  const label = () => props.label || 'No sale data';
  const glyph = () => props.glyph || '🏚️';

  const [open, setOpen] = createSignal(false);
  const [data, setData] = createSignal(null);
  const [loading, setLoading] = createSignal(false);
  const [loaded, setLoaded] = createSignal(false);

  function ensureLoaded() {
    if (loaded() || loading()) return;
    if (!slug()) return;
    setLoading(true);
    loadKnown(slug()).then(d => {
      setData(d);
      setLoaded(true);
      setLoading(false);
    });
  }

  function show() {
    setOpen(true);
    ensureLoaded();
  }
  function hide() {
    setOpen(false);
  }

  // Allowlist of fact kinds we render — keeps the placeholder safe
  // even if the backend evolves and starts surfacing user-level facts.
  const SAFE_KINDS = new Set([
    'first-seen', 'last-sale', 'sales-count',
    'seller-count', 'recent-kiosks',
  ]);
  const safeFacts = () => (data()?.knownFacts || []).filter(f => SAFE_KINDS.has(f.kind));

  return (
    <span
      class="v2-mg-placeholder"
      tabIndex={0}
      role="button"
      aria-label={`${label()} for ${slug()} — hover for details`}
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocusIn={show}
      onFocusOut={hide}
    >
      <span class="v2-mg-placeholder__glyph" aria-hidden="true">{glyph()}</span>
      <span class="v2-mg-placeholder__label">{label()}</span>
      <Show when={open()}>
        <div class="v2-mg-tip" role="tooltip">
          <div class="v2-mg-tip__scanlines" aria-hidden="true" />
          <div class="v2-mg-tip__inner">
            <div class="v2-mg-tip__header">What we know · {slug()}</div>
            <Show when={!loading()} fallback={
              <div class="v2-mg-tip__empty">Loading…</div>
            }>
              <Show when={safeFacts().length} fallback={
                <div class="v2-mg-tip__empty">
                  No non-sale facts on record yet.
                </div>
              }>
                <dl class="v2-mg-tip__list">
                  <For each={safeFacts().filter(f => f.kind !== 'recent-kiosks')}>{(f) => (
                    <div class="v2-mg-tip__row">
                      <dt>{f.label}</dt>
                      <dd>{fmtFactValue(f)}</dd>
                    </div>
                  )}</For>
                </dl>
                <Show when={safeFacts().find(f => f.kind === 'recent-kiosks')}>
                  {(fact) => (
                    <>
                      <div class="v2-mg-tip__header" style={{ 'margin-top': '6px' }}>
                        Recent kiosks
                      </div>
                      <ul class="v2-mg-tip__kiosks">
                        <For each={(fact().value || []).slice(0, 4)}>{(k) => {
                          const where = [k.zone, k.city].filter(Boolean).join(' · ');
                          return (
                            <li>
                              {where || k.trader || 'unknown'}
                              <Show when={k.weeksHeld}>
                                <span style={{ opacity: 0.65 }}> · {k.weeksHeld}w</span>
                              </Show>
                            </li>
                          );
                        }}</For>
                      </ul>
                    </>
                  )}
                </Show>
              </Show>
            </Show>
          </div>
        </div>
      </Show>
    </span>
  );
}
